import { Link } from 'react-router';
import { GuideLayout, GuideSection, GuideTip } from '@/components/guides/guide-layout';
import { PhaseFiveGuideNotes } from '@/components/guides/phase-five-guide-notes';

const inlineLink =
  'font-medium text-brand-600 underline decoration-brand-300 underline-offset-2';

export function EnglandWalesPotholeClaimsGuide() {
  return (
    <GuideLayout
      slug="england-wales-pothole-claims"
      category="Legal"
      categoryColor="success"
      title="Pothole Claims in England vs Wales"
      seoTitle="Pothole Claims in England vs Wales | Fixtyer"
      description="The shared legal framework and the practical road-authority, data and submission differences between England and Wales."
      readTime="7 min"
      updatedDate="2026-07-19"
      reviewedDate="2026-07-19"
      datePublished="2026-07-19"
      reviewedFor="England and Wales"
      contentOwner="Elevyn Technology Group Limited editorial team"
      sources={[
        {
          name: 'Highways Act 1980 (Wales extent)',
          url: 'https://www.legislation.gov.uk/ukpga/1980/66/contents/wales',
        },
        {
          name: 'Traffic Wales strategic-road claims guidance',
          url: 'https://traffic.wales/claims',
        },
        {
          name: 'Traffic Wales report-an-issue form',
          url: 'https://traffic.wales/report-issue',
        },
        {
          name: 'GOV.UK vehicle-damage claim route',
          url: 'https://www.gov.uk/claim-for-damage-to-your-vehicle',
        },
      ]}
      atAGlance={[
        {
          label: 'Answer first',
          value:
            'England and Wales share the Highways Act 1980 framework, but the strategic-road authority, local submission route and available public datasets differ.',
        },
        {
          label: 'England strategic roads',
          value: 'National Highways manages the English strategic road network.',
        },
        {
          label: 'Wales strategic roads',
          value:
            'The Welsh Ministers are highway authority for Welsh motorways and trunk roads; councils handle other Welsh local roads.',
        },
      ]}
      relatedGuides={[
        {
          slug: 'national-highways-pothole-claims',
          title: 'National Highways Claims',
          readTime: '7 min',
        },
        {
          slug: 'highway-authority-claim-routes',
          title: 'Claim Routes Directory',
          readTime: '6 min',
        },
        {
          slug: 'section-58-defence',
          title: 'Section 58 Defence',
          readTime: '7 min',
        },
      ]}
    >
      <GuideSection title="What is shared" icon={<span aria-hidden="true">=</span>}>
        <p className="mb-4">
          Sections 41 and 58 of the Highways Act 1980 extend to England and Wales. A claimant still
          needs evidence connecting the incident, alleged highway defect, damage and claimed loss.
          An authority may rely on Section 58 if it can establish that it took the care reasonably
          required in the circumstances.
        </p>
        <p>
          The practical question is not simply which country the road is in. Identify the authority
          that maintained that specific road, then follow its current submission instructions.
        </p>
      </GuideSection>

      <GuideSection title="What differs in practice" icon={<span aria-hidden="true">↔</span>}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="p-3">Task</th>
                <th className="p-3">England</th>
                <th className="p-3">Wales</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200">
              <tr>
                <td className="p-3 font-medium">Motorway or trunk road</td>
                <td className="p-3">Check whether National Highways manages it.</td>
                <td className="p-3">Check the Welsh Government strategic road network.</td>
              </tr>
              <tr>
                <td className="p-3 font-medium">Other local road</td>
                <td className="p-3">Use the relevant English highway authority route.</td>
                <td className="p-3">Use the relevant Welsh county or county-borough route.</td>
              </tr>
              <tr>
                <td className="p-3 font-medium">Ratings data</td>
                <td className="p-3">DfT 2025-26 ratings cover English highway authorities.</td>
                <td className="p-3">Do not import those England-only ratings into a Welsh claim.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </GuideSection>

      <GuideSection title="Choose the correct official route" icon={<span aria-hidden="true">✓</span>}>
        <p className="mb-4">
          Search the{' '}
          <Link to="/guides/highway-authority-claim-routes" className={inlineLink}>
            verified claim-routes directory
          </Link>
          . Coverage is partial by design, so use GOV.UK or the authority&apos;s own site when an
          authority is not yet listed.
        </p>
        <GuideTip variant="info" title="Welsh strategic-road claims">
          Traffic Wales explains that the Welsh Ministers are highway authority for Welsh motorways
          and trunk roads. Read its{' '}
          <a
            href="https://traffic.wales/claims"
            target="_blank"
            rel="noopener noreferrer"
            className={inlineLink}
          >
            strategic-road claims guidance
          </a>
          , then select “Claims” in the{' '}
          <a
            href="https://traffic.wales/report-issue"
            target="_blank"
            rel="noopener noreferrer"
            className={inlineLink}
          >
            dedicated report-an-issue form
          </a>
          . Use the separate carriageway-defect option to report the road fault, and do not use a
          local-council form for a Welsh motorway or trunk-road incident.
        </GuideTip>
      </GuideSection>

      <PhaseFiveGuideNotes />
    </GuideLayout>
  );
}
